/**
 * Business – Börse
 * /business/boerse
 *
 * Dynamische Marktpreise für Produkte, Tabakwaren, Fundgut und Schwarzmarkt.
 * Aus Crime Ops hierher verschoben (Prompt 07). Inhalt unverändert übernommen.
 */

import { api } from '../../services/api.js';
import { renderResponsiveTable, injectResponsiveTableStyles } from '../../utils/responsive-table.js';
import type { RtRow } from '../../utils/responsive-table.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

type DemandLevel = 'SEHR_NIEDRIG' | 'NIEDRIG' | 'NORMAL' | 'HOCH' | 'SEHR_HOCH';

interface MarketPriceItem {
  key:            string;
  label:          string;
  category:       'HANF' | 'LABOR' | 'FUNDGUT' | 'HEHLER' | 'SCHWARZMARKT' | 'TABAK' | 'LEGAL_LABOR';
  currency:       'NOX' | 'BLACK_MONEY';
  illegal:        boolean;
  unit:           string;
  priceCents:     string;
  basePriceCents: string;
  modifier:       number;
  demand:         DemandLevel;
  isTagesFavorit: boolean;
  isFeatured:     boolean;
  changePercent:  number;
  trendSymbol:    '▲' | '▼' | '►';
  sparkline:      string;
  volumeLevel:    0 | 1 | 2 | 3;
  volumePerTick:  number;
  demandIndex:    number;
}

interface MarketPricesData {
  items:           MarketPriceItem[];
  featuredKeys:    string[];
  nextUpdateAt:    string;
  lastTickAt:      string;
  dailyHighlights: {
    tagesFavorit: string[];
    gefragt:      string[];
  };
}

interface PriceHistoryEntry { time: string; price: string; demandIndex: number; }
interface ItemHistoryData   { itemKey: string; entries: PriceHistoryEntry[]; high24h: string; low24h: string; }

// NT-BOERSE-FOKUS-01: drei Waren, Montag 06:00 gezogen, pro Woche ein Kontingent je Ware.
interface WochenauftragItem {
  itemKey:     string;
  label:       string;
  zielMenge:   number;
  erledigt:    number;
  bonusCapped: boolean;
}
interface WochenauftragData {
  items:       WochenauftragItem[];
  weekStartAt: string;
  weekEndsAt:  string;
}

type BoerseTab = 'rohstoffe' | 'fundgut-beute' | 'schwarzmarkt' | 'tabak-legal';

interface CrimeOpsStatusPartial {
  schwarzmarkt: { unlocked: boolean };
}

// ─── State ────────────────────────────────────────────────────────────────────

let boerseData:          MarketPricesData | null = null;
let wochenauftragData:   WochenauftragData | null = null;
let boerseHistory:       ItemHistoryData | null  = null;
let selectedBoerseItem:  string | null           = null;
let selectedBoerseTab:   BoerseTab               = 'rohstoffe';
let schwarzmarktUnlocked                         = false;
let tickInterval:        ReturnType<typeof setInterval> | null = null;
// Bricht die vorherige Preisverlauf-Anfrage ab, sobald eine neue Zeile angeklickt wird —
// verhindert einen Anfragen-Stau (+ veraltete Antworten überschreiben die Auswahl) beim
// schnellen Durchklicken mehrerer Items.
let historyAbortController: AbortController | null = null;

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'FERTIG';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtSeurShort(cents: string): string {
  const n = parseInt(cents, 10);
  if (n >= 100_000_00) return `${String(Math.round(n / 100_000_00))} Mio S-₦`;
  return `${(n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} S-₦`;
}

function fmtNoxShort(cents: string): string {
  const n = parseInt(cents, 10);
  if (n >= 100_000_00) return `${String(Math.round(n / 100_000_00))} Mio ₦`;
  return `${(n / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₦`;
}

function fmtMarketPriceShort(cents: string, currency: 'NOX' | 'BLACK_MONEY'): string {
  return currency === 'BLACK_MONEY' ? fmtSeurShort(cents) : fmtNoxShort(cents);
}

function demandDisplay(item: MarketPriceItem): string {
  const CONFIG: Record<DemandLevel, { label: string; color: string }> = {
    SEHR_HOCH:    { label: 'NACHFRAGE: SEHR HOCH',   color: 'var(--accent-gold)'   },
    HOCH:         { label: 'NACHFRAGE: HOCH',         color: 'var(--accent-orange)' },
    NORMAL:       { label: 'NACHFRAGE: NORMAL',       color: 'var(--text-muted)'    },
    NIEDRIG:      { label: 'NACHFRAGE: NIEDRIG',      color: 'var(--accent-red)'    },
    SEHR_NIEDRIG: { label: 'NACHFRAGE: SEHR NIEDRIG', color: 'var(--accent-red)'    },
  };
  const { label, color } = CONFIG[item.demand];
  return `<span style="font-family:var(--font-mono);font-size:11px;letter-spacing:0.06em;color:${color};white-space:nowrap;">${label}</span>`;
}

// ─── SVG-Preisverlauf ─────────────────────────────────────────────────────────

function renderSvgChart(
  entries:           PriceHistoryEntry[],
  label:             string,
  basePriceCents:    string,
  currentPriceCents: string,
  high24hCents:      string,
  low24hCents:       string,
): string {
  const W = 260; const H = 72;
  const PAD_LEFT = 4; const PAD_RIGHT = 16; const PAD_TOP = 6; const PAD_BOTTOM = 16;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;

  const prices    = entries.map((e) => parseInt(e.price, 10));
  const base      = parseInt(basePriceCents, 10);
  const allPrices = [...prices, base];
  const minP      = Math.min(...allPrices) * 0.96;
  const maxP      = Math.max(...allPrices) * 1.04;
  const range     = maxP - minP || 1;

  const toX = (i: number): number => PAD_LEFT + (i / Math.max(prices.length - 1, 1)) * chartW;
  const toY = (p: number): number => PAD_TOP  + chartH - ((p - minP) / range) * chartH;

  const points    = prices.map((p, i) => `${toX(i).toFixed(1)},${toY(p).toFixed(1)}`).join(' ');
  const baseY     = toY(base).toFixed(1);
  const lastPrice = prices[prices.length - 1] ?? base;
  const lineColor = lastPrice >= base ? '#52b788' : '#e63946';

  const t0 = entries[0]?.time ?? '--:--';
  const t1 = entries[entries.length - 1]?.time ?? '--:--';

  const maxLabel = fmtSeurShort(high24hCents);
  const minLabel = fmtSeurShort(low24hCents);
  const curLabel = fmtSeurShort(currentPriceCents);

  const lastX = toX(prices.length - 1).toFixed(1);
  const lastY = toY(lastPrice).toFixed(1);

  return `
    <div style="position:relative;margin-top:var(--space-2);background:var(--bg-surface);border:1px solid var(--border);padding:var(--space-2);border-radius:var(--radius-sm);">
      <!-- 24H HOCH links oben, 24H TIEF links unten (fest positioniert, kein Überlappen) -->
      <div style="position:absolute;top:var(--space-2);left:var(--space-2);font-family:var(--font-mono);line-height:1.3;pointer-events:none;">
        <div style="font-size:9px;color:var(--accent-green);opacity:0.85;">24H HOCH</div>
        <div style="font-size:10px;color:var(--accent-green);font-weight:bold;">${esc(maxLabel)}</div>
      </div>
      <div style="position:absolute;bottom:calc(var(--space-2) + 18px);left:var(--space-2);font-family:var(--font-mono);line-height:1.3;pointer-events:none;">
        <div style="font-size:9px;color:var(--accent-red);opacity:0.85;">24H TIEF</div>
        <div style="font-size:10px;color:var(--accent-red);font-weight:bold;">${esc(minLabel)}</div>
      </div>
      <div style="display:flex;justify-content:flex-end;margin-bottom:4px;">
        <span style="font-size:var(--font-size-xs);color:var(--accent-gold);font-family:var(--font-mono);letter-spacing:0.07em;">PREISVERLAUF: ${esc(label.toUpperCase())}</span>
      </div>
      <svg viewBox="0 0 ${String(W)} ${String(H)}" width="100%" height="${String(H)}" style="display:block;overflow:visible;">
        <!-- Basislinie (gestrichelt) -->
        <line x1="${String(PAD_LEFT)}" y1="${baseY}" x2="${String(W - PAD_RIGHT)}" y2="${baseY}"
          stroke="#6b6b80" stroke-width="0.7" stroke-dasharray="3,3" />
        <!-- Fläche unter der Kurve -->
        <polygon
          points="${points} ${toX(prices.length - 1).toFixed(1)},${String(H - PAD_BOTTOM)} ${toX(0).toFixed(1)},${String(H - PAD_BOTTOM)}"
          fill="${lineColor}" fill-opacity="0.08" />
        <!-- Kurve -->
        <polyline points="${points}" fill="none" stroke="${lineColor}" stroke-width="1.5"
          stroke-linejoin="round" stroke-linecap="round" />
        <!-- Letzter Punkt: Dot -->
        <circle cx="${lastX}" cy="${lastY}" r="2.5" fill="${lineColor}" />
        <!-- Aktueller Preis: klein rechts neben Dot -->
        <text x="${(parseFloat(lastX) + 4).toFixed(1)}" y="${(parseFloat(lastY) + 3.5).toFixed(1)}"
          font-size="8" fill="#f0b429" font-family="monospace" font-weight="bold">${esc(curLabel)}</text>
        <!-- Zeitachse -->
        <text x="${String(PAD_LEFT)}" y="${String(H - 3)}" font-size="8" fill="#6b6b80" font-family="monospace">${esc(t0)}</text>
        <text x="${String(W - PAD_RIGHT - 2)}" y="${String(H - 3)}" font-size="8" fill="#6b6b80" font-family="monospace" text-anchor="end">${esc(t1)}</text>
      </svg>
    </div>
  `;
}

// ─── Wochenauftrag-Banner (NT-BOERSE-FOKUS-01) ────────────────────────────────

function renderWochenauftragBanner(): string {
  if (!wochenauftragData || wochenauftragData.items.length === 0) return '';

  const remainingMs = Math.max(0, new Date(wochenauftragData.weekEndsAt).getTime() - Date.now());
  const rows = wochenauftragData.items.map((item) => {
    const done    = Math.min(item.erledigt, item.zielMenge);
    const full    = done >= item.zielMenge;
    const barColor = full ? 'var(--accent-green)' : 'var(--accent-purple)';
    const cappedLabel = item.bonusCapped
      ? '<span style="margin-left:8px;padding:1px 4px;border:1px solid var(--accent-gold);color:var(--accent-gold);font-size:10px;letter-spacing:0.06em;border-radius:var(--radius-sm);">BONUS GEDECKELT</span>'
      : '';
    return `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:var(--space-2);padding:3px 0;flex-wrap:wrap;">
        <span style="color:${barColor};font-family:var(--font-mono);font-size:var(--font-size-sm);font-weight:bold;">${esc(item.label.toUpperCase())}${cappedLabel}</span>
        <span style="font-family:var(--font-mono);font-size:var(--font-size-sm);color:${full ? 'var(--accent-green)' : 'var(--text-primary)'};white-space:nowrap;">${String(done)}/${String(item.zielMenge)}</span>
      </div>
    `;
  }).join('');

  return `
    <div style="background:color-mix(in srgb, var(--accent-purple) 6%, transparent);border:1px solid color-mix(in srgb, var(--accent-purple) 30%, transparent);border-radius:var(--radius-sm);padding:var(--space-2) var(--space-3);margin-bottom:var(--space-3);">
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;">
        <span style="font-size:var(--font-size-xs);color:var(--text-muted);letter-spacing:0.1em;font-family:var(--font-mono);">WOCHENAUFTRAG</span>
        <span style="font-size:var(--font-size-xs);color:var(--text-muted);font-family:var(--font-mono);">RESTLAUFZEIT <span id="wa-countdown" style="color:var(--text-primary);">${fmtCountdown(remainingMs)}</span></span>
      </div>
      ${rows}
    </div>
  `;
}

// ─── Börse-Inhalt rendern ─────────────────────────────────────────────────────

function renderBoerseContent(): string {
  if (!boerseData) {
    return '<p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:0.08em;">LADE...</p>';
  }

  const { items, nextUpdateAt, dailyHighlights } = boerseData;

  const tabs: { id: BoerseTab; label: string }[] = [
    { id: 'rohstoffe',     label: 'PRODUKTE'          },
    { id: 'tabak-legal',   label: 'TABAKWAREN & LEGAL' },
    { id: 'fundgut-beute', label: 'FUNDGUT & BEUTE'   },
  ];
  if (schwarzmarktUnlocked) {
    tabs.push({ id: 'schwarzmarkt', label: 'SCHWARZMARKT' });
  }

  if (!tabs.some((t) => t.id === selectedBoerseTab)) {
    selectedBoerseTab  = 'rohstoffe';
    selectedBoerseItem = null;
    boerseHistory      = null;
  }

  const now          = Date.now();
  const nextUpdateMs = Math.max(0, new Date(nextUpdateAt).getTime() - now);

  // ─── Zwei-Zeilen-Banner: TAGES-FAVORIT + GEFRAGT ─────────────────────────
  const tagesFavoritItems = items.filter((i) => dailyHighlights.tagesFavorit.includes(i.key));
  const gefragtItems      = items.filter((i) => dailyHighlights.gefragt.includes(i.key));

  const tagesFavoritLine = tagesFavoritItems.length > 0
    ? tagesFavoritItems.map((i) => {
        const sign = i.modifier >= 0 ? '+' : '';
        return `★ ${esc(i.label.toUpperCase())}   ${sign}${String(i.modifier)}%`;
      }).join(' · ')
    : '';

  const gefragtLine = gefragtItems.length > 0
    ? gefragtItems.map((i) => esc(i.label.toUpperCase())).join(' · ')
    : '';

  const featuredBanner = (tagesFavoritLine || gefragtLine)
    ? `<div style="background:color-mix(in srgb, var(--accent-gold) 6%, transparent);border:1px solid color-mix(in srgb, var(--accent-gold) 30%, transparent);border-radius:var(--radius-sm);padding:var(--space-2) var(--space-3);margin-bottom:var(--space-3);">
        ${tagesFavoritLine ? `<div style="display:flex;gap:var(--space-3);align-items:baseline;margin-bottom:4px;">
          <span style="font-size:var(--font-size-xs);color:var(--text-muted);letter-spacing:0.1em;font-family:var(--font-mono);white-space:nowrap;min-width:10ch;">TAGES-FAVORIT</span>
          <span style="font-size:var(--font-size-sm);color:var(--accent-gold);font-family:var(--font-mono);font-weight:bold;">${tagesFavoritLine}</span>
        </div>` : ''}
        ${gefragtLine ? `<div style="display:flex;gap:var(--space-3);align-items:baseline;">
          <span style="font-size:var(--font-size-xs);color:var(--text-muted);letter-spacing:0.1em;font-family:var(--font-mono);white-space:nowrap;min-width:10ch;">GEFRAGT</span>
          <span style="font-size:var(--font-size-xs);color:var(--accent-orange);font-family:var(--font-mono);">${gefragtLine}</span>
        </div>` : ''}
      </div>`
    : '';

  // Zeitzone-Label (MEZ = Winter / MESZ = Sommer)
  const nextDate = new Date(nextUpdateAt);
  const tzName   = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', timeZoneName: 'short',
  }).formatToParts(nextDate).find((p) => p.type === 'timeZoneName')?.value ?? 'MEZ';

  const nextUpdateEl = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-3);font-family:var(--font-mono);font-size:12px;color:var(--text-muted);">
      <span>TAGES-RESET 06:00 ${esc(tzName)}</span>
      <span id="ml-next-update" style="color:var(--text-primary);font-size:13px;">${fmtCountdown(nextUpdateMs)}</span>
    </div>
  `;

  const tabSelector = `
    <div style="display:flex;gap:var(--space-2);margin-top:var(--space-2);margin-bottom:var(--space-3);flex-wrap:wrap;">
      ${tabs.map((tab) => {
        const active = tab.id === selectedBoerseTab;
        return `<button
          type="button"
          data-boerse-action="switch-tab"
          data-ml-tab="${esc(tab.id)}"
          style="border:1px solid ${active ? 'var(--accent-gold)' : 'var(--border)'};background:${active ? 'color-mix(in srgb, var(--accent-gold) 12%, transparent)' : 'var(--bg-surface)'};color:${active ? 'var(--accent-gold)' : 'var(--text-muted)'};padding:4px 10px;border-radius:var(--radius-btn);font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;">
          ${esc(tab.label)}
        </button>`;
      }).join('')}
    </div>
  `;

  // ─── Item-Sektionen nach Tab ──────────────────────────────────────────────
  const ELEKTRONIK_KEYS = new Set(['smartphone_guenstig', 'smartphone_mittel', 'smartphone_teuer', 'laptop_alt', 'laptop_neu']);
  const SCHMUCK_KEYS    = new Set(['armbanduhr_einfach', 'armbanduhr_wertvoll', 'schmuck_kette', 'schmuck_ring', 'bargeld_buendel']);
  const WAFFEN_KEYS     = new Set(['waffe_pistole']);
  const SCHMUGGEL_KEYS  = new Set(['schmuggelware_pakete', 'schmuggelware_koffer', 'zigaretten_unverzollt', 'designerware', 'alkohol_unverzollt']);
  const EXKLUSIVA_KEYS  = new Set(['elektronik_gestohlen', 'medikamente_eu', 'exotische_tiere', 'kunstfaelschungen']);

  // Record über die exakte Qualitätsstufen-Union (statt Record<number,...>) — dot-/Index-
  // Zugriff mit einem 1|2|3|4|5-typisierten Schlüssel bleibt dadurch garantiert nicht-undefined.
  const ML_Q_MOD: Record<1 | 2 | 3 | 4 | 5, number> = { 1: 1.00, 2: 1.20, 3: 1.40, 4: 1.70, 5: 2.00 };
  const ML_Q_LABEL: Record<1 | 2 | 3 | 4 | 5, { label: string; color: string }> = {
    1: { label: 'AUSSCHUSS',      color: 'var(--text-muted)'    },
    2: { label: 'HINTERHOF-WARE', color: 'var(--accent-red)'    },
    3: { label: 'MARKTWARE',      color: 'var(--accent-orange)' },
    4: { label: 'FEINWARE',       color: 'var(--accent-green)'  },
    5: { label: 'PREMIUM-ERNTE',  color: 'var(--accent-gold)'   },
  };

  interface MlSection { label: string; items: MarketPriceItem[] }

  const DEMAND_NAME_COLOR: Record<DemandLevel, string> = {
    SEHR_HOCH:    'var(--accent-gold)',
    HOCH:         'var(--accent-orange)',
    NORMAL:       'var(--text-primary)',
    NIEDRIG:      'var(--text-muted)',
    SEHR_NIEDRIG: 'var(--accent-red)',
  };

  const renderRow = (item: MarketPriceItem): RtRow => {
    const trendColor =
      item.modifier > 0  ? 'var(--accent-green)' :
      item.modifier < 0  ? 'var(--accent-red)'   :
      'var(--text-muted)';
    const trendSign    = item.modifier >= 0 ? '+' : '';
    const isSel        = selectedBoerseItem === item.key;
    const priceColor   = item.currency === 'BLACK_MONEY' ? 'var(--accent-bm)' : 'var(--accent-gold)';
    const nameColor    = DEMAND_NAME_COLOR[item.demand];
    const starPrefix   = item.demand === 'SEHR_HOCH' ? '★ ' : '';
    const illegalBadge = item.illegal
      ? '<span style="margin-left:6px;padding:1px 4px;border:1px solid var(--accent-red);color:var(--accent-red);font-size:10px;letter-spacing:0.06em;border-radius:var(--radius-sm);">ILLEGAL</span>'
      : '';
    const chartHtml = isSel
      ? boerseHistory
        ? renderSvgChart(boerseHistory.entries, item.label, item.basePriceCents, item.priceCents, boerseHistory.high24h, boerseHistory.low24h)
        : '<p style="font-size:10px;color:var(--text-muted);padding:4px 0;font-family:var(--font-mono);">LADE VERLAUF...</p>'
      : '';

    return {
      tableHtml: `
        <tr style="border-top:1px solid var(--border);cursor:pointer;" data-boerse-action="show-history" data-itemkey="${esc(item.key)}">
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:var(--font-size-sm);color:${nameColor};">
            ${starPrefix}${esc(item.label.toUpperCase())}${illegalBadge}
          </td>
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:var(--font-size-sm);color:${priceColor};white-space:nowrap;font-weight:bold;">
            ${esc(fmtMarketPriceShort(item.priceCents, item.currency))}
          </td>
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:var(--font-size-sm);color:${trendColor};white-space:nowrap;">
            ${esc(item.trendSymbol)} ${trendSign}${String(item.modifier)}%
          </td>
          <td style="padding:8px 6px;">${demandDisplay(item)}</td>
        </tr>
        ${isSel ? `<tr><td colspan="4" style="padding:0 4px 8px 4px;">${chartHtml}</td></tr>` : ''}
      `,
      rowAttrs: `data-boerse-action="show-history" data-itemkey="${esc(item.key)}" style="cursor:pointer;"`,
      cardHeadHtml: `<span class="rt-card-name" style="color:${nameColor};">${starPrefix}${esc(item.label.toUpperCase())}</span>${illegalBadge}`,
      cardFields: [
        { label: 'PREIS',     value: `<span style="color:${priceColor};font-weight:bold;">${esc(fmtMarketPriceShort(item.priceCents, item.currency))}</span>` },
        { label: 'TREND',     value: `<span style="color:${trendColor};">${esc(item.trendSymbol)} ${trendSign}${String(item.modifier)}%</span>` },
        { label: 'NACHFRAGE', value: demandDisplay(item) },
      ],
      cardBottomHtml: chartHtml
        ? `<div style="padding-top:var(--space-2);border-top:1px solid var(--border);margin-top:var(--space-2);">${chartHtml}</div>`
        : '',
    };
  };

  // Spezieller Renderer für TABAK-Items: zeigt Q1–Q5 Preisstaffel inline
  const renderTabakRow = (item: MarketPriceItem): RtRow => {
    const baseCents  = parseInt(item.priceCents, 10);
    const trendColor =
      item.modifier > 0 ? 'var(--accent-green)' :
      item.modifier < 0 ? 'var(--accent-red)'   :
      'var(--text-muted)';
    const trendSign = item.modifier >= 0 ? '+' : '';
    const qualRows  = ([1, 2, 3, 4, 5] as const).map((tier) => {
      const q = ML_Q_LABEL[tier];
      const p = Math.round(baseCents * ML_Q_MOD[tier]);
      return `<tr>
        <td style="padding:3px 6px 3px 20px;font-family:var(--font-mono);font-size:11px;color:${q.color};letter-spacing:0.04em;">
          Q${String(tier)} ${q.label}
        </td>
        <td style="padding:3px 6px;font-family:var(--font-mono);font-size:11px;color:var(--accent-gold);white-space:nowrap;">
          ${esc(fmtNoxShort(String(p)))}
        </td>
        <td colspan="2"></td>
      </tr>`;
    }).join('');
    const cardQualFields = ([1, 2, 3, 4, 5] as const)
      .map((tier) => {
        const q = ML_Q_LABEL[tier];
        const p = Math.round(baseCents * ML_Q_MOD[tier]);
        return `<span class="rt-label" style="color:${q.color};">Q${String(tier)}</span>`
          + `<span class="rt-value" style="color:var(--accent-gold);">${esc(fmtNoxShort(String(p)))}</span>`;
      })
      .join('');

    return {
      tableHtml: `
        <tr style="border-top:1px solid var(--border);">
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:13px;color:var(--text-primary);">
            ${esc(item.label.toUpperCase())}
          </td>
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:11px;color:var(--text-muted);white-space:nowrap;">
            BASIS: ${esc(fmtNoxShort(item.priceCents))}
          </td>
          <td style="padding:8px 6px;font-family:var(--font-mono);font-size:13px;color:${trendColor};white-space:nowrap;">
            ${esc(item.trendSymbol)} ${trendSign}${String(item.modifier)}%
          </td>
          <td style="padding:8px 6px;">${demandDisplay(item)}</td>
        </tr>
        ${qualRows}
      `,
      cardHeadHtml: `<span class="rt-card-name">${esc(item.label.toUpperCase())}</span>`,
      cardFields: [
        { label: 'BASIS (Q1)', value: `<span style="color:var(--accent-gold);">${esc(fmtNoxShort(item.priceCents))}</span>` },
        { label: 'TREND',      value: `<span style="color:${trendColor};">${esc(item.trendSymbol)} ${trendSign}${String(item.modifier)}%</span>` },
      ],
      cardBottomHtml: `<div class="rt-card-body" style="margin-top:var(--space-1);border-top:1px solid var(--border);padding-top:var(--space-1);">${cardQualFields}</div>`,
    };
  };

  const renderSection = (section: MlSection, isTabak = false): string => `
    <div style="margin-bottom:var(--space-4);">
      <p style="font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.1em;color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-2) 0;padding-bottom:4px;border-bottom:1px solid var(--border);">
        ${esc(section.label)}
      </p>
      ${renderResponsiveTable(
        [{ header: 'WARE' }, { header: isTabak ? 'BASISPREIS (Q1)' : 'PREIS' }, { header: 'TREND' }, { header: 'NACHFRAGE' }],
        isTabak ? section.items.map(renderTabakRow) : section.items.map(renderRow),
      )}
    </div>
  `;

  // ─── Tab "tabak-legal": Sonderbehandlung ohne MlSection-Mechanismus ──────
  if (selectedBoerseTab === 'tabak-legal') {
    const tabakItems      = items.filter((i) => i.category === 'TABAK');
    const legalLaborItems = items.filter((i) => i.category === 'LEGAL_LABOR');

    const zigarettenItems = tabakItems.filter((i) => i.key.startsWith('zigarettenschachtel_'));
    const zigarreItems    = tabakItems.filter((i) => i.key.startsWith('zigarrenkiste_'));

    const tabakHtml = [
      zigarettenItems.length > 0
        ? renderSection({ label: 'ZIGARETTENSCHACHTELN (42 STK.)', items: zigarettenItems })
        : '',
      zigarreItems.length > 0
        ? renderSection({ label: 'ZIGARRENKISTEN (6 STK.)', items: zigarreItems })
        : '',
      legalLaborItems.length > 0
        ? `<div style="margin-bottom:var(--space-4);">
            <p style="font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.1em;color:var(--text-muted);text-transform:uppercase;margin:0 0 var(--space-2) 0;padding-bottom:4px;border-bottom:1px solid var(--border);">LEGALE LABORPRODUKTE</p>
            ${renderResponsiveTable(
              [{ header: 'WARE' }, { header: 'PREIS' }, { header: 'TREND' }, { header: 'NACHFRAGE' }],
              legalLaborItems.map(renderRow),
            )}
          </div>`
        : '',
    ].filter(Boolean).join('');

    const tabakHint = `
      <div style="margin-top:var(--space-3);padding-top:var(--space-2);border-top:1px solid var(--border);font-family:var(--font-mono);font-size:12px;color:var(--text-muted);line-height:1.6;">
        Basispreis = Q1 (Ausschuss). Qualitätsmodifikatoren: Q1×1,0 / Q2×1,2 / Q3×1,4 / Q4×1,7 / Q5×2,0.
        Preise schwanken täglich. Verkauf über STRASSENHANDEL.
      </div>`;

    return `
      ${renderWochenauftragBanner()}
      ${featuredBanner}
      ${nextUpdateEl}
      ${tabSelector}
      ${tabakHtml.length > 0 ? tabakHtml : '<p class="co-empty">NOCH KEINE MARKTDATEN VERFÜGBAR.</p>'}
      ${tabakHint}
    `;
  }

  const sections: MlSection[] = (() => {
    if (selectedBoerseTab === 'rohstoffe') {
      return [
        { label: 'ANBAU', items: items.filter((i) => i.category === 'HANF') },
        { label: 'LABOR', items: items.filter((i) => i.category === 'LABOR') },
      ];
    }
    if (selectedBoerseTab === 'fundgut-beute') {
      return [
        { label: 'MATERIALIEN', items: items.filter((i) => i.category === 'FUNDGUT') },
        { label: 'ELEKTRONIK',  items: items.filter((i) => ELEKTRONIK_KEYS.has(i.key)) },
        { label: 'SCHMUCK',     items: items.filter((i) => SCHMUCK_KEYS.has(i.key)) },
      ];
    }
    return [
      { label: 'WAFFEN',    items: items.filter((i) => WAFFEN_KEYS.has(i.key)) },
      { label: 'SCHMUGGEL', items: items.filter((i) => SCHMUGGEL_KEYS.has(i.key)) },
      { label: 'EXKLUSIVA', items: items.filter((i) => EXKLUSIVA_KEYS.has(i.key)) },
    ];
  })().filter((s) => s.items.length > 0);

  const allTabItems = sections.flatMap((s) => s.items);
  if (selectedBoerseItem && !allTabItems.some((i) => i.key === selectedBoerseItem)) {
    selectedBoerseItem = null;
    boerseHistory      = null;
  }

  const contentHtml = sections.length > 0
    ? sections.map((s) => renderSection(s)).join('')
    : '<p class="co-empty">FÜR DIESEN TAB SIND NOCH KEINE WAREN VERFÜGBAR.</p>';

  const legendHtml = `
    <div style="margin-top:var(--space-3);padding-top:var(--space-2);border-top:1px solid var(--border);font-family:var(--font-mono);font-size:12px;color:var(--text-muted);display:flex;gap:var(--space-4);flex-wrap:wrap;">
      <span style="color:var(--accent-green);">▲</span><span>steigend</span>
      <span style="color:var(--accent-red);">▼</span><span>fallend</span>
      <span>► stabil</span>
      <span style="color:var(--accent-gold);">★ Tages-Favorit</span>
      <span style="color:var(--text-italic);">Zeile anklicken = Preisverlauf</span>
    </div>
  `;

  return `
    ${renderWochenauftragBanner()}
    ${featuredBanner}
    ${nextUpdateEl}
    ${tabSelector}
    ${contentHtml}
    ${legendHtml}
  `;
}

// ─── Re-Render ────────────────────────────────────────────────────────────────

function rerender(): void {
  const root = document.getElementById('boerse-root');
  if (!root) return;
  root.innerHTML = renderBoerseContent();
  attachBoerseListeners(root);
}

// ─── Event-Handler ────────────────────────────────────────────────────────────

function attachBoerseListeners(root: HTMLElement): void {
  root.addEventListener('click', (e) => {
    const target = e.target as Element;
    const btn = target.closest<HTMLElement>('[data-boerse-action]');
    if (!btn) return;

    const action = btn.dataset.boerseAction;

    if (action === 'switch-tab') {
      const nextTab = btn.dataset.mlTab as BoerseTab | undefined;
      if (!nextTab) return;
      if (selectedBoerseTab !== nextTab) {
        selectedBoerseTab  = nextTab;
        selectedBoerseItem = null;
        boerseHistory      = null;
        rerender();
      }
      return;
    }

    if (action === 'show-history') {
      const itemKey = btn.dataset.itemkey ?? '';

      // Laufende Anfrage IMMER zuerst abbrechen — verhindert Anfragen-Stau + veraltete
      // Antworten beim schnellen Durchklicken mehrerer Zeilen.
      historyAbortController?.abort();
      historyAbortController = null;

      if (selectedBoerseItem === itemKey) {
        selectedBoerseItem = null;
        boerseHistory      = null;
        rerender();
        return;
      }

      selectedBoerseItem = itemKey;
      boerseHistory      = null;
      rerender();

      const controller = new AbortController();
      historyAbortController = controller;
      api.get<ItemHistoryData>(`/market-economy/prices/${encodeURIComponent(itemKey)}/history`, controller.signal)
        .then((data) => {
          if (selectedBoerseItem !== itemKey) return; // Auswahl hat sich inzwischen geändert
          boerseHistory = data;
          rerender();
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return; // erwartet, kein Fehlerzustand
          boerseHistory = null;
        });
    }
  });
}

// ─── Countdown-Ticker ─────────────────────────────────────────────────────────

function tickBoerse(): void {
  if (!boerseData) return;
  const now       = Date.now();
  const nextEl    = document.getElementById('ml-next-update');
  if (nextEl) {
    const remaining = Math.max(0, new Date(boerseData.nextUpdateAt).getTime() - now);
    nextEl.textContent = fmtCountdown(remaining);
  }
  const waEl = document.getElementById('wa-countdown');
  if (waEl && wochenauftragData) {
    const remaining = Math.max(0, new Date(wochenauftragData.weekEndsAt).getTime() - now);
    waEl.textContent = fmtCountdown(remaining);
  }
  // Alle 30 Min Preise neu laden
  const lastTick = new Date(boerseData.lastTickAt).getTime();
  if (now - lastTick >= 30 * 60_000) {
    void loadBoerseData();
  }
}

// ─── Daten laden ─────────────────────────────────────────────────────────────

async function loadBoerseData(): Promise<void> {
  try {
    boerseData = await api.get<MarketPricesData>('/market-economy/prices');
    if (selectedBoerseItem) {
      boerseHistory = await api.get<ItemHistoryData>(
        `/market-economy/prices/${encodeURIComponent(selectedBoerseItem)}/history`
      );
    }
  } catch { /* still ignorieren */ }
  try {
    wochenauftragData = await api.get<WochenauftragData>('/market-economy/wochenauftrag');
  } catch { wochenauftragData = null; }
  rerender();
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export function renderBoerseTab(): string {
  return `
    <section class="dash-content" style="padding:0;" id="boerse-root">
      <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:0.08em;">LADE...</p>
    </section>
  `;
}

export function mountBoerseTab(): void {
  injectResponsiveTableStyles();
  void (async () => {
    try {
      const status = await api.get<CrimeOpsStatusPartial>('/crime-ops/status');
      schwarzmarktUnlocked = status.schwarzmarkt.unlocked;
    } catch {
      schwarzmarktUnlocked = false;
    }
    await loadBoerseData();
  })();
  tickInterval = setInterval(tickBoerse, 1_000);
}

export function cleanupBoerse(): void {
  if (tickInterval !== null) { clearInterval(tickInterval); tickInterval = null; }
  historyAbortController?.abort();
  historyAbortController = null;
  boerseData         = null;
  wochenauftragData  = null;
  boerseHistory      = null;
  selectedBoerseItem = null;
  schwarzmarktUnlocked = false;
}
